import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { FaEye, FaSyncAlt } from "react-icons/fa";
import hook from "../Assets/hook.png";
import BASE_URL from "../utils/Urls";
import AccessModal from "./AccessModal";
import BankDetailsModal from "./BankDetailsModel";
import ConfirmModal from "./ConfirmModal";
import KeypadModal from "./KeypadModal";
import PasswordManagementModal from "./PasswordManagementModal";

const STATUS_META = [
  { key: "unfilled", label: "Unfilled", dot: "bg-amber-400" },
  { key: "unverified", label: "Unverified", dot: "bg-orange-400" },
  { key: "training", label: "Training", dot: "bg-violet-400" },
  { key: "active", label: "Active", dot: "bg-emerald-400" },
  { key: "inactive", label: "Inactive", dot: "bg-slate-300" },
  { key: "deactivate", label: "Deactivated", dot: "bg-rose-400" },
];

export default function EyeIconBigPopup({ user, onClose, refreshKey = 0 }) {
  const [showKeypad, setShowKeypad] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMain, setShowMain] = useState(true);
  const [bankDetails, setBankDetails] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [stats, setStats] = useState({
    unfilled: 0,
    unverified: 0,
    training: 0,
    active: 0,
    inactive: 0,
    deactivate: 0,
    total: 0,
    bankedTotal: 0,
    paidEarnings: 0,
    totalLivePayoutBill: 0,
    totalPaymentDue: 0,
    totalDue: 0,
    totalTickets: 0,
    totalTicketUsers: 0,
  });

  const editedBankedEarningsCount = user?.todayBankedEarningsCount ?? 0;
  const editedPaidEarningsCount = user?.todayPaidEarningsCount ?? 0;
  const [showAccess, setShowAccess] = useState(false);

  const [, setLockLoaded] = useState(false);
  useEffect(() => {
    const fetchLock = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/locks/`);
        if (res.data?.lock) {
          console.log("🔐 Primary lock found:", res.data.lock);
          setLockLoaded(true);
        }
      } catch (err) {
        console.error("❌ Failed to fetch primary lock:", err);
      }
    };
    fetchLock();
  }, []);

  const loadStats = useCallback(async () => {
      try {
        const res = await axios.get(`${BASE_URL}/users/`);
        const allUsers = res.data?.users || [];

        const totalLivePayoutBill = allUsers.reduce((sum, u) => {
          const val = parseFloat(u.livePayoutBills || 0);
          return sum + (isNaN(val) ? 0 : val);
        }, 0);
        const totalBankEarnings = allUsers.reduce((sum, u) => {
          const val = parseFloat(u.bankedEarnings || 0);
          return sum + (isNaN(val) ? 0 : val);
        }, 0);

        const totalPaymentDue = allUsers.reduce((sum, u) => {
          const val = parseFloat(u.paymentDue || 0);
          return sum + (isNaN(val) ? 0 : val);
        }, 0);

        let totalTickets = 0;
        let totalTicketUsers = 0;
        try {
          const ticketRes = await axios.get(`${BASE_URL}/tickets/count/all`);
          totalTickets = ticketRes.data.total_tickets || 0;
          totalTicketUsers = ticketRes.data.total_users || 0;
        } catch (err) {
          console.error("❌ Failed to fetch total tickets count", err);
        }

        const counts = {
          unfilled: allUsers.filter(
            (u) => u.status_code === 0 && u.status === "unfilled"
          ).length,

          unverified: allUsers.filter(
            (u) =>
              (u.status_code === 1 ||
                u.status_code === 2 ||
                u.status_code === 3 ||
                u.status_code === 4) &&
              u.status === "unfilled"
          ).length,

          training: allUsers.filter(
            (u) =>
              u.status === "unfilled" &&
              u.status_code === 5 &&
              u.rejected.length === 0 &&
              Array.isArray(u.approved) &&
              u.approved.length === 4 &&
              u.token === null
          ).length,

          deactivate: allUsers.filter((u) => u.status_code === -1).length,

          active: allUsers.filter(
            (u) =>
              (u.status === "active" || u.status === "ACTIVE") &&
              u.status_code === 6 &&
              u.login_image !== null
          ).length,

          inactive: allUsers.filter(
            (u) =>
              u.status_code === 6 &&
              (u.status === "active" || u.status === "ACTIVE") &&
              u.login_image === null
          ).length,

          total: allUsers.length,
          totalBankEarnings: totalBankEarnings,
          totalLivePayoutBill: totalLivePayoutBill,
          totalPaymentDue: totalPaymentDue,
          totalDue: totalLivePayoutBill + totalPaymentDue,

          bankedTotal: allUsers.reduce((sum, u) => {
            const val = parseFloat(u.bankedEarnings || 0);
            return sum + (isNaN(val) ? 0 : val);
          }, 0),
          paidEarnings: allUsers.reduce((sum, u) => {
            const val = parseFloat(u.paidEarnings || 0);
            return sum + (isNaN(val) ? 0 : val);
          }, 0),
          totalTickets: totalTickets,
          totalTicketUsers: totalTicketUsers,
        };

        setStats(counts);
      } catch (err) {
        console.error("❌ Failed to fetch user stats", err);
      }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshKey]);

  const loadBankDetails = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/admin/bank-details`);
      setBankDetails(res.data.result || []);
    } catch (err) {
      console.error("❌ Failed to fetch bank details", err);
    }
  }, []);

  useEffect(() => {
    loadBankDetails();
  }, [loadBankDetails]);

  const [refreshing, setRefreshing] = useState(false);
  const handleManualRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadStats(), loadBankDetails()]);
    setRefreshing(false);
  };

  const handleCloseAll = () => {
    setShowMain(false);
    setShowKeypad(false);
    setShowConfirm(false);
    onClose?.();
  };

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pressTimer, setPressTimer] = useState(null);

  const handleMouseDown = () => {
    const timer = setTimeout(() => {
      setShowPasswordModal(true);
    }, 2000);
    setPressTimer(timer);
  };

  const handleMouseUp = () => {
    clearTimeout(pressTimer);
  };

  const total = stats.total || 0;

  const moneyTiles = [
    {
      key: "bank",
      label: "Bank earnings",
      value: `$${stats.bankedTotal ?? 0}`,
      accent: "text-indigo-600",
      sub: `Edited · ${editedBankedEarningsCount}`,
    },
    {
      key: "paid",
      label: "Paid out",
      value: `$${stats.paidEarnings ?? 0}`,
      accent: "text-emerald-600",
      sub: null,
    },
    {
      key: "live",
      label: "Live payout",
      value: `$${stats.totalLivePayoutBill ?? 0}`,
      accent: "text-amber-600",
      sub: `Edited · ${editedPaidEarningsCount}`,
    },
    {
      key: "due",
      label: "Payment due",
      value: `$${stats.totalPaymentDue ?? 0}`,
      accent: "text-rose-600",
      sub: `Edited · ${editedBankedEarningsCount}`,
    },
    {
      key: "tickets",
      label: "Tickets",
      value: `${stats.totalTickets ?? 0}`,
      accent: "text-sky-600",
      sub: `${stats.totalTicketUsers ?? 0} users`,
    },
  ];

  return (
    <>
      {showMain && (
        <div className="fixed left-0 right-0 top-0 z-50 w-full">
          <div className="w-full border-b border-slate-200 bg-white shadow-lg">
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
              <FaEye className="text-slate-400" />
              <h2 className="text-base font-semibold text-slate-800">
                Network overview
              </h2>
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
              >
                <FaSyncAlt className={refreshing ? "animate-spin" : ""} />
                {refreshing ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            <div className="border-b border-slate-100 px-5 py-4">
              <div className="mb-2.5 flex items-baseline justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  Promoter status
                </p>
                <p className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-800">{total}</span>{" "}
                  total
                </p>
              </div>

              <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                {total > 0 &&
                  STATUS_META.map((s) => {
                    const count = stats[s.key] || 0;
                    if (!count) return null;
                    return (
                      <div
                        key={s.key}
                        className={s.dot}
                        style={{ width: `${(count / total) * 100}%` }}
                        title={`${s.label}: ${count}`}
                      />
                    );
                  })}
              </div>

              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                {STATUS_META.map((s) => (
                  <div key={s.key} className="flex items-center gap-1.5 text-sm">
                    <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                    <span className="text-slate-500">{s.label}</span>
                    <span className="font-semibold text-slate-800">
                      {stats[s.key] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 px-5 py-4 sm:grid-cols-3 lg:grid-cols-5">
              {moneyTiles.map((t) => (
                <div
                  key={t.key}
                  className="rounded-xl border border-slate-200 bg-slate-50/60 p-3"
                >
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    {t.label}
                  </p>
                  <p className={`mt-1 text-xl font-bold ${t.accent}`}>
                    {t.value}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    {t.sub || " "}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 bg-slate-50 px-5 py-3">
              <button
                onClick={() => setShowKeypad(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-rose-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-rose-600"
              >
                Exit <span className="text-xs">✕</span>
              </button>

              <button
                onClick={() => setShowAccess(true)}
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
              >
                Access
              </button>
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                  Due
                </span>
                <div className="text-sm font-semibold text-slate-700">
                  <span className="text-amber-600">
                    {stats.totalLivePayoutBill ?? 0}.00
                  </span>{" "}
                  +{" "}
                  <span className="text-rose-600">
                    {stats.totalPaymentDue ?? 0}.00
                  </span>{" "}
                  ={" "}
                  <span className="text-slate-900">
                    {stats.totalDue ?? 0}.00
                  </span>
                </div>
              </div>

              
              <div
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                className="cursor-pointer opacity-0 transition-opacity hover:opacity-30"
                aria-hidden="true"
              >
                <img src={hook} alt="" className="h-7 w-9" />
              </div>
            </div>
          </div>
        </div>
      )}

      {showAccess && <AccessModal onClose={() => setShowAccess(false)} />}
      {showPasswordModal && (
        <PasswordManagementModal onClose={() => setShowPasswordModal(false)} />
      )}

      {selectedBank && (
        <BankDetailsModal
          bank={selectedBank}
          onClose={() => setSelectedBank(null)}
          onUpdated={(updated) => {
            setBankDetails((prev) =>
              prev.map((b) => (b.id === updated.id ? updated : b))
            );
            setSelectedBank(null);
          }}
        />
      )}
      {showKeypad && (
        <KeypadModal
          type="primary"
          onGoClick={() => {
            setShowConfirm(true);
            setShowKeypad(false);
          }}
          onClose={() => setShowKeypad(false)}
        />
      )}
      {showConfirm && (
        <ConfirmModal onYes={handleCloseAll} onNo={() => setShowConfirm(false)} />
      )}
    </>
  );
}
